import { getClientLicenseListField } from '@supply-mover-2-0/shared';
import type { ListBuilderColumn } from '../../types';

/**
 * Shared column definitions for client lists
 */

/**
 * Client name column with sales status dot, linked name, and address info
 */
export const createClientNameColumn = (): ListBuilderColumn => ({
	id: 'client-name',
	fields: [
		{
			id: 'field_1751488137880',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'salesStatus',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				valueMapping: [
					{
						color: { themeColor: 'success' },
						value: '1',
					},
					{
						color: { themeColor: 'danger' },
						value: '0',
					},
				],
			},
			displayStyle: 'dot',
			floatingLeft: true,
		},
		{
			id: 'name-field',
			link: {
				type: 'object',
				enabled: true,
				objectTable: 'client',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'name',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'address1',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'address1',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'field_1750195778831',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1751399430207',
			dataConfig: {
				type: 'template',
				template: '{client.city}, {client.state} {client.zip}',
				templateFields: [],
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{
			id: 'h1',
			text: 'Name',
		},
	],
	minWidth: 180,
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'name',
			objectTable: 'client',
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Location column with lead/customer badge and business type
 */
export const createLocationColumn = (): ListBuilderColumn => ({
	id: 'location',
	fields: [
		{
			id: 'field-client-id',
			displayStyle: 'badge',
			dataConfig: {
				type: 'template',
				template:
					'{leadOrCustomer:Customer=C,Lead=L}#{externalId2|externalId|id}',
				templateFields: [
					{ fieldName: 'leadOrCustomer' },
					{ fieldName: 'externalId2' },
					{ fieldName: 'externalId' },
					{ fieldName: 'id' },
				],
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: {
								fieldName: 'leadOrCustomer',
							},
							operator: 'equals',
							value: 'Customer',
							ignoreCase: true,
							color: { type: 'info' },
						},
						{
							field: {
								fieldName: 'leadOrCustomer',
							},
							operator: 'equals',
							value: 'Lead',
							ignoreCase: true,
							color: { type: 'warning' },
						},
					],
				},
				variant: 'solid',
			},
		},
		{
			id: 'field_1750394549312',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1750361746643',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'businessType',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'static',
					staticColor: { type: 'neutral' },
				},
			},
			displayStyle: 'badge',
		},
	],
	headers: [
		{
			id: 'h3-lead-cust',
			text: 'Lead / Cust',
		},
		{
			id: 'h3-line-break',
			text: '',
		},
		{
			id: 'h3-business-type',
			text: 'Business Type',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'leadOrCustomer',
			objectTable: 'client',
		},
		{
			direction: 'ASC',
			fieldName: 'name',
			objectTable: 'client',
		},
	],
	verticalAlignment: 'top',
});

/**
 * Lifecycle stage column with custom selector component
 */
export const createLifecycleStageColumn = (): ListBuilderColumn => ({
	id: 'lifecycle-stage',
	fields: [
		{
			id: 'lifecycle-stage-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lifecycleStageName',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: {
				id: 'client-lifecycle-stage-selector',
			},
		},
	],
	headers: [
		{
			id: 'lifecycle-stage-header',
			text: 'Stage',
		},
	],
	sortable: true,
	alignment: 'center',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'fkClientLifecycleStageId',
			objectTable: 'client',
			nullsLast: true,
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'small',
	},
	verticalAlignment: 'top',
});

export interface SalespeopleColumnOptions {
	/**
	 * Render the Lifecycle Stage selector as a third line under Salesperson /
	 * Alternate (PD-13163). The stage used to be a column of its own, which —
	 * next to the new 1Y Sales sparkline — pushed the default client list into
	 * horizontal scrolling on an ordinary window. Salesperson / Alternate is
	 * two lines tall in a row that is already three (name / street /
	 * city-state-zip), so the stage rides along for free and a whole column
	 * comes back.
	 *
	 * Off by default: only the standard client list opted in. The routing,
	 * ownership and activity lists never carried a stage column and should not
	 * gain one.
	 */
	includeStage?: boolean;
}

/**
 * Salespeople column with primary and alternate salesperson, optionally
 * carrying the lifecycle stage selector as a third line.
 */
export const createSalespeopleColumn = ({
	includeStage = false,
}: SalespeopleColumnOptions = {}): ListBuilderColumn => ({
	id: 'salespeople',
	fields: [
		{
			id: 'salesperson-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'salespersonName',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-1',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'alternate-salesperson-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'alternateSalespersonName',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		...(includeStage
			? [
					{
						id: 'line-break-2',
						dataConfig: {
							type: 'custom_text' as const,
							customText: '',
						},
						styleConfig: {
							size: 'md' as const,
							colorRule: {
								type: 'default' as const,
							},
						},
						displayStyle: 'line_break' as const,
					},
					{
						id: 'lifecycle-stage-name',
						dataConfig: {
							type: 'field' as const,
							field: {
								fieldName: 'lifecycleStageName',
								objectTable: 'client',
							},
						},
						displayStyle: 'custom' as const,
						customComponent: {
							id: 'client-lifecycle-stage-selector',
						},
					},
				]
			: []),
	],
	headers: [
		{
			id: 'salesperson-header',
			text: 'Salesperson',
		},
		{
			id: 'header-line-break',
			text: '',
		},
		{
			id: 'alternate-salesperson-header',
			text: 'Alternate',
		},
		...(includeStage
			? [
					{
						id: 'header-line-break-2',
						text: '',
					},
					{
						id: 'lifecycle-stage-header',
						text: 'Stage',
					},
				]
			: []),
	],
	// The stage selector brings its sort with it; the salespeople names never
	// had one, so the column's single sort is unambiguously the stage sort and
	// the indicator renders against the "Stage" header line.
	sortable: includeStage,
	alignment: 'left',
	...(includeStage
		? {
				sortFields: [
					{
						direction: 'ASC' as const,
						fieldName: 'fkClientLifecycleStageId',
						objectTable: 'client',
						nullsLast: true,
					},
				],
			}
		: {}),
	widthConfig: {
		mode: 'flexible',
		preset: 'medium',
	},
	verticalAlignment: 'top',
});

/**
 * Contact info column with phone and email
 */
export const createContactInfoColumn = (): ListBuilderColumn => ({
	id: 'contact-info',
	fields: [
		{
			id: 'phone',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'phone',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'phone',
		},
		{
			id: 'line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'email',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'email',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'email',
		},
	],
	headers: [
		{
			id: 'h2-phone',
			text: 'Phone',
		},
		{
			id: 'h2-line-break',
			text: '',
		},
		{
			id: 'h2-email',
			text: 'Email',
		},
	],
	sortable: false,
	alignment: 'left',
	verticalAlignment: 'top',
});

/**
 * Reorder column with average invoice revenue and forecasted reorder date
 */
export const createReorderColumn = (): ListBuilderColumn => ({
	id: 'reorder',
	fields: [
		{
			id: 'field_1750475570885',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'forecast',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'currency',
		},
		{
			id: 'field_1750475618703',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'field_1750475452349',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'forecastedReorderDate',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'relative_date',
		},
	],
	headers: [
		{
			id: 'header_1750475608556',
			text: 'Reorder Due',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'forecastedReorderDate',
			objectTable: 'client',
			nullsLast: true,
		},
	],
	verticalAlignment: 'top',
});

/**
 * Task complete column with complete action button
 */
export const createTaskCompleteColumn = (): ListBuilderColumn => ({
	id: 'task-complete-column',
	fields: [
		{
			id: 'complete-action',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'complete-task-action',
					label: 'Complete',
					iconName: 'check',
					callbackName: 'completeTask',
					variant: 'success',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'h-complete',
			text: '',
		},
	],
	width: 50,
	align: 'center',
	sortable: false,
});

/**
 * Next activity column — shows the task subject and start time.
 *
 * Note: the priority badge used to live here as a third stacked field. It now
 * lives in the dedicated Status/Priority chip column ({@link createNextActivityChipsColumn})
 * so the user can change priority inline, alongside the To-Do/Done status
 * chip. Removing it from this column also keeps the activity cell visually
 * lighter — just the subject + time.
 */
export const createNextActivityColumn = (): ListBuilderColumn => ({
	id: 'next-activity-column',
	fields: [
		{
			id: 'activity-title',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityTitle',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'activity-line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'line_break',
		},
		{
			id: 'activity-time',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityTime',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
				formatOptions: {
					dateFormat: 'weekday-short-with-time',
					timeField: 'nextActivityTimeOfDay',
					timezoneField: 'nextActivityTimezone',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [
		{
			id: 'h-next-activity',
			text: 'Next Activity',
		},
	],
	sortable: true,
	alignment: 'left',
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'nextActivityTime',
			objectTable: 'task_data',
		},
	],
	widthConfig: {
		mode: 'flexible',
		preset: 'large',
	},
	verticalAlignment: 'top',
});

/**
 * Status + Priority chip column for the activity-based client lists
 * (Scheduled Today, Overdue). Replaces the legacy "green check" complete
 * button and the priority badge that used to sit inside the Next Activity
 * cell.
 *
 * Layout (one column, two stacked chips):
 *   [To-Do ▾]
 *   [Medium ▾]
 *
 * Both chips read from denormalized task fields on the client row
 * (`nextActivityTaskId`, `nextActivityPriority`). The custom components are
 * registered in
 *   modules/clients/src/components/list-builder/registerComponents.ts.
 */
export const createNextActivityChipsColumn = (): ListBuilderColumn => ({
	id: 'next-activity-chips-column',
	fields: [
		{
			id: 'next-activity-status-chip',
			dataConfig: {
				type: 'field',
				field: {
					// No real "status" field — server filters _isCompleted = 0, so
					// the chip is effectively always "pending" / To-Do. We still
					// reference nextActivityTaskId so the selector can read it
					// straight off the field's value if it ever needs to.
					fieldName: 'nextActivityTaskId',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'client-next-activity-status-selector' },
		},
		{
			id: 'next-activity-chips-line-break',
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'next-activity-priority-chip',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'nextActivityPriority',
					objectTable: 'client',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'client-next-activity-priority-selector' },
		},
	],
	// Two stacked header labels mirror the stacked chips below: Status sits
	// over the To-Do/Done chip and Priority sits over the Low/Medium/High chip.
	headers: [
		{ id: 'h-next-activity-status', text: 'Status' },
		{ id: 'h-next-activity-priority', text: 'Priority' },
	],
	width: 110,
	align: 'center',
	sortable: false,
});

/**
 * Client actions column with quickView, edit, and recover actions
 */
export const createClientActionsColumn = (): ListBuilderColumn => ({
	id: 'actions-column',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'quick-view-action',
					label: 'Quick View',
					iconName: 'eye',
					callbackName: 'quickView',
					variant: 'secondary',
					visibility: 'visible',
				},
				{
					id: 'edit-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'edit',
					variant: 'primary',
					visibility: 'visible',
					conditionalVisibility: {
						field: 'deletedDate',
						condition: 'is_null',
					},
				},
				{
					id: 'recover-action',
					label: 'Recover',
					iconName: 'undo',
					callbackName: 'recoverClient',
					variant: 'secondary',
					visibility: 'visible',
					conditionalVisibility: {
						field: 'deletedDate',
						condition: 'is_not_null',
					},
				},
				{
					id: 'remove-client-action',
					label: 'Remove from Group',
					iconName: 'trash',
					callbackName: 'removeClient',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'actions-header',
			text: '',
		},
	],
	sortable: false,
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'middle',
});

/**
 * Task actions column with edit and delete task actions
 */
export const createTaskActionsColumn = (): ListBuilderColumn => ({
	id: 'actions-column',
	fields: [
		{
			id: 'actions-dropdown',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: {
					type: 'default',
				},
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'edit-task-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'editTask',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-task-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'deleteTask',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [
		{
			id: 'h-actions',
			text: '',
		},
	],
	width: 100,
	align: 'center',
	sortable: false,
});

/**
 * Proximity column for near-me list
 */
export const createProximityColumn = (): ListBuilderColumn => ({
	id: 'proximity',
	fields: [
		{
			id: 'distance-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'proximity',
					objectTable: 'client',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'default',
				},
				formatOptions: {
					suffix: ' mi',
					decimalPlaces: 1,
				},
			},
			displayStyle: 'number',
		},
	],
	headers: [
		{
			id: 'h-proximity',
			text: 'Distance',
		},
	],
	sortable: true,
	alignment: 'right',
	// PD-13545 — match the backend's computed alias `proximity` everywhere it's
	// referenced. clients.service.ts adds the haversine distance to the SELECT as
	// `… AS proximity` and excludes `client.proximity` from the field list. The
	// list-builder emits the sort fieldName verbatim into ORDER BY, and the display
	// field + the list's requiredFields into the SELECT — so all three must say
	// `proximity`. `distanceFromCenter` is neither a real column nor an alias, so
	// it produced `ORDER BY client.distanceFromCenter` (Chris's first fix) AND
	// leaked into the SELECT field list (this fix) → "Unknown column ...".
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'proximity',
			objectTable: 'client',
		},
	],
	widthConfig: {
		mode: 'fixed',
		width: 100,
	},
	verticalAlignment: 'middle',
});

/**
 * Standard column set for most client lists
 * Includes: client-name, location, lifecycle-stage, salespeople, contact-info, reorder, actions
 */
/**
 * PD-15504 — which live routes cover this client, for the bundled 'Routing'
 * list. Mirrors the same column on the orders Routing list so the two read
 * identically; ClientsService flattens the memberships onto rows as `routes`
 * ([{ id, name }]).
 *
 * "Live" is the point: a client covered only by a paused, expired or archived
 * route shows as having none, because none of those will dispatch. A column
 * that counted them would answer the wrong question.
 */
// PD-15698 — one column carries both halves of route coverage: which routes
// cover the client, and when a driver last actually stopped there. Stacked
// fields instead of a new column so the Routing list's real estate stays flat.
export const createClientRoutesColumn = (): ListBuilderColumn => ({
	id: 'client-routes',
	fields: [
		{
			id: 'client-routes-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'routes', objectTable: 'client' },
				// Empty string (not the default '-') so the display component's
				// emptyText handles the no-routes state.
				defaultValue: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'related_entity_list',
			displayConfig: {
				entityType: 'route',
				icon: 'route',
				linkPattern: '/distribution/route/{id}',
				nameField: 'name',
				maxDisplay: 2,
				emptyText: 'Not routed',
			},
		},
		{
			id: 'client-routes-line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'client-last-stop-label',
			dataConfig: {
				type: 'custom_text',
				customText: 'Last stop:',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
			// Hide the label (not just the value) for never-visited clients so
			// the cell reads "Not routed" alone instead of a dangling caption.
			conditionalVisibility: {
				field: 'lastStopDate',
				condition: 'is_not_null',
			},
		},
		{
			id: 'client-last-stop-date',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'lastStopDate', objectTable: 'client' },
				defaultValue: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				// M/D/YY h:mm AM/PM — the time of day matters here: it is the
				// completion stamp of the driver's visit, not just a date.
				formatOptions: { dateFormat: 'datetime-short' },
			},
			displayStyle: 'datetime',
		},
	],
	headers: [{ id: 'h-client-routes', text: 'Routes / Last Stop' }],
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'lastStopDate', objectTable: 'client' },
	],
});

/**
 * PD-15924 — which live SALES routes cover this client, stacked over the next
 * planned sales stop. Mirrors the delivery Routes / Last Stop column so the
 * two Routing lists read the same way; ClientsService attaches `salesRoutes`
 * ([{ id, name }]) and computes `nextSalesStopDate` only when asked.
 */
export const createClientSalesRoutesColumn = (): ListBuilderColumn => ({
	id: 'client-sales-routes',
	fields: [
		{
			id: 'client-sales-routes-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'salesRoutes', objectTable: 'client' },
				defaultValue: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'related_entity_list',
			displayConfig: {
				entityType: 'route',
				icon: 'route',
				linkPattern: '/sales-routes?tab=manage',
				nameField: 'name',
				maxDisplay: 2,
				emptyText: 'Not on a sales route',
			},
		},
		{
			id: 'client-sales-routes-line-break',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'client-next-sales-stop-label',
			dataConfig: {
				type: 'custom_text',
				customText: 'Next stop:',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
			},
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'nextSalesStopDate',
				condition: 'is_not_null',
			},
		},
		{
			id: 'client-next-sales-stop-date',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'nextSalesStopDate', objectTable: 'client' },
				defaultValue: '',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'theme', themeColor: 'textSecondary' },
				// A trip DATE, not an instant: the stop's time of day lives on the
				// trip and is shown in the day editor.
				formatOptions: { dateFormat: 'short' },
			},
			displayStyle: 'date',
		},
	],
	headers: [{ id: 'h-client-sales-routes', text: 'Sales Routes / Next Stop' }],
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'nextSalesStopDate', objectTable: 'client' },
	],
});

/**
 * PD-15924 — a plain date column over one of the denormalised client recency
 * columns (PD-15918): `lastVisitDate` or `lastDeliveryDate`.
 */
export const createClientRecencyDateColumn = (
	fieldName: 'lastVisitDate' | 'lastDeliveryDate',
	header: string,
): ListBuilderColumn => ({
	id: `client-${fieldName}`,
	fields: [
		{
			id: `client-${fieldName}-field`,
			dataConfig: {
				type: 'field',
				field: { fieldName, objectTable: 'client' },
				defaultValue: '—',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { dateFormat: 'short' },
			},
			displayStyle: 'date',
		},
	],
	headers: [{ id: `h-client-${fieldName}`, text: header }],
	sortable: true,
	sortFields: [{ direction: 'DESC', fieldName, objectTable: 'client' }],
	widthConfig: { mode: 'fixed', width: 120 },
});

/**
 * PD-17191 — when anyone last used the storefront for this account
 * (`client.lastStorefrontActivityDate`), as a relative date, or a green
 * "Online now" pill while one of its users is on a storefront. The
 * `storefront-last-active` component reads the server-attached `isOnlineNow`
 * / `onlineStorefrontNames` off the row; ClientsService attaches them whenever
 * this field is requested.
 *
 * Opt-in: on the Recently Online list and in the field picker, not on the
 * default lists (most tenants' accounts never use a storefront).
 */
export const createLastStorefrontActivityColumn = (): ListBuilderColumn => ({
	id: 'client-lastStorefrontActivityDate',
	fields: [
		{
			id: 'client-lastStorefrontActivityDate-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'lastStorefrontActivityDate',
					objectTable: 'client',
				},
				defaultValue: 'No storefront activity',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'custom',
			customComponent: { id: 'storefront-last-active' },
		},
	],
	headers: [
		{ id: 'h-client-lastStorefrontActivityDate', text: 'Last Storefront Activity' },
	],
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'lastStorefrontActivityDate',
			objectTable: 'client',
		},
	],
	widthConfig: { mode: 'fixed', width: 170 },
});

/**
 * PD-17191 — storefront visits started in the last 30 days, a computed alias
 * ClientsService selects only when this column or its sort asks for it.
 */
export const createStorefrontVisits30dColumn = (): ListBuilderColumn => ({
	id: 'client-storefrontVisits30d',
	fields: [
		{
			id: 'client-storefrontVisits30d-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'storefrontVisits30d', objectTable: 'client' },
				defaultValue: '0',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: { numberFormat: 'auto' },
			},
			displayStyle: 'number',
		},
	],
	headers: [
		{ id: 'h-client-storefrontVisits30d', text: 'Storefront Visits (30d)' },
	],
	sortable: true,
	alignment: 'right',
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'storefrontVisits30d',
			objectTable: 'client',
		},
	],
	widthConfig: { mode: 'fixed', width: 130 },
});

/**
 * PD-16077 — a column over one of the per-license-type client fields
 * (`deaLicenseNumber`, `deaLicenseExpirationDate`, `stateBoardLicenseNumber`,
 * …). The value is a computed alias the clients service resolves from
 * `client_license`; see CLIENT_LICENSE_LIST_FIELDS.
 *
 * Why a ready-made definition: dropping a field onto a column in the List
 * Configuration modal always produces a TEXT field, which prints an expiration
 * date as a raw timestamp. These pair the field with the right display style
 * (short date for an expiration, text for a number) so the column reads
 * correctly without hand-configuring it.
 *
 * Deliberately NOT on any default client list — the columns are opt-in
 * per-tenant, added through List Configuration (PD-16077).
 */
export const createClientLicenseColumn = (
	fieldName: string,
	header?: string,
): ListBuilderColumn => {
	const licenseField = getClientLicenseListField(fieldName);
	const isExpiration = licenseField?.kind === 'expirationDate';

	return {
		id: `client-${fieldName}`,
		fields: [
			{
				id: `client-${fieldName}-field`,
				dataConfig: {
					type: 'field',
					field: { fieldName, objectTable: 'client' },
					defaultValue: '—',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					...(isExpiration ? { formatOptions: { dateFormat: 'short' } } : {}),
				},
				displayStyle: isExpiration ? 'date' : 'text',
			},
		],
		headers: [
			{
				id: `h-client-${fieldName}`,
				text: header ?? licenseField?.fieldLabel ?? fieldName,
			},
		],
		sortable: true,
		sortFields: [{ direction: 'ASC', fieldName, objectTable: 'client' }],
		widthConfig: { mode: 'fixed', width: 140 },
	};
};

/**
 * PD-16077 — the pair the ticket asked for: "DEA License" and "DEA Expiration
 * Date", ready to drop into a list configuration.
 */
export const createClientDeaLicenseColumns = (): Array<ListBuilderColumn> => [
	createClientLicenseColumn('deaLicenseNumber'),
	createClientLicenseColumn('deaLicenseExpirationDate'),
];

/**
 * PD-13163 — "1Y Sales": the client's trailing 12 complete months of invoiced
 * revenue as a sparkline, restoring the column SM1's client list carried. The
 * series comes from the `client_sales` monthly rollup, attached server-side.
 *
 * Bound to `client.id` because the chart is self-contained: it reads the
 * `monthlyData` / `trend` the server attaches to the row (declared in
 * componentFieldRequirements), not the configured data source.
 *
 * It ships on the lead-bearing lists too. A lead has no invoices, so its cell
 * falls through to the chart's own no-data state instead of being special-cased.
 */
export const createSalesTrendColumn = (): ListBuilderColumn => ({
	id: 'salesTrend',
	fields: [
		{
			id: 'sales-trend-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'id',
					objectTable: 'client',
				},
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'custom',
			customComponent: { id: 'client-sales-trend-chart' },
		},
	],
	headers: [
		{
			id: 'header-sales-trend',
			text: '1Y Sales',
		},
	],
	sortable: false,
	alignment: 'left',
	widthConfig: { mode: 'flexible', preset: 'medium' },
	verticalAlignment: 'middle',
});

export const createStandardClientColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createSalespeopleColumn({ includeStage: true }),
	createContactInfoColumn(),
	createReorderColumn(),
	createSalesTrendColumn(),
	createClientActionsColumn(),
];

/**
 * Column set without lifecycle stage
 * Includes: client-name, location, salespeople, contact-info, reorder, actions
 */
export const createClientColumnsNoLifecycle = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createReorderColumn(),
	createSalesTrendColumn(),
	createClientActionsColumn(),
];

/**
 * Column set for activity-based lists (scheduled today, overdue).
 *
 * Order: client name, status/priority chips, location, salespeople, contact
 * info, next activity (subject + time), task actions.
 *
 * The Status + Priority chip stack (see {@link createNextActivityChipsColumn})
 * sits in the second column. It replaces the legacy green-check complete
 * column; the priority badge was also pulled out of the Next Activity cell so
 * it can be edited inline alongside the status chip.
 *
 * Reorder Due is intentionally omitted on these lists — these tabs are about
 * scheduled / overdue activity, not buying cadence, so the reorder column
 * just adds noise.
 */
export const createActivityListColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createNextActivityChipsColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createNextActivityColumn(),
	createSalesTrendColumn(),
	createTaskActionsColumn(),
];

/**
 * Column set for near-me list
 * Includes: client-name, location, salespeople, contact-info, reorder, proximity, actions
 */
export const createNearMeColumns = (): Array<ListBuilderColumn> => [
	createClientNameColumn(),
	createLocationColumn(),
	createSalespeopleColumn(),
	createContactInfoColumn(),
	createReorderColumn(),
	createProximityColumn(),
	createSalesTrendColumn(),
	createClientActionsColumn(),
];
